import { ref, get, set, remove, database } from "../config/firebase";
import { DataTypeVerbete as Verbete } from "../hooks/sinalario";

export interface UserVideo {
  id: string;
  title: string;
  description: string;
  videoUrl: string;
  thumbnailUrl?: string;
  imageUrl?: string;
  createdAt: number;
  updatedAt?: number;
  userId: string;
}

type FavoriteItem = Verbete & {
  favoritedAt?: number;
  isFavorite?: boolean;
};

function favoriteKey(item: Partial<FavoriteItem> | string): string {
  if (typeof item === "string") return item;
  return String(item.uid || item.palavra || "").trim();
}

function normalizeFavoritesList(raw: unknown): FavoriteItem[] {
  if (!raw) return [];
  let list: unknown[] = [];
  if (Array.isArray(raw)) {
    list = raw;
  } else if (typeof raw === "object") {
    list = Object.values(raw as Record<string, unknown>);
  }

  return list.filter(
    (item): item is FavoriteItem =>
      !!item &&
      typeof item === "object" &&
      !!((item as FavoriteItem).uid || (item as FavoriteItem).palavra)
  );
}

function dedupeFavorites(items: FavoriteItem[]): FavoriteItem[] {
  const seen = new Set<string>();
  const out: FavoriteItem[] = [];
  items.forEach((item) => {
    const key = favoriteKey(item);
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push(item);
  });
  return out;
}

/* eslint-disable class-methods-use-this */
class FavoritesService {
  /** Path canônico do app mobile: `favorites/{uid}` (array). */
  private appFavoritesPath(userId: string) {
    return `favorites/${userId}`;
  }

  /** Path legado da web: `users/{uid}/favorites`. */
  private legacyFavoritesPath(userId: string) {
    return `users/${userId}/favorites`;
  }

  private appVideosPath(userId: string) {
    return `userVideos/${userId}`;
  }

  private legacyVideosPath(userId: string) {
    return `users/${userId}/repository`;
  }

  private async readFavoritesRaw(userId: string): Promise<FavoriteItem[]> {
    const primaryRef = ref(database, this.appFavoritesPath(userId));
    const primarySnap = await get(primaryRef);
    let list = primarySnap.exists()
      ? normalizeFavoritesList(primarySnap.val())
      : [];

    // Fallback / merge com path legado da web
    const legacyRef = ref(database, this.legacyFavoritesPath(userId));
    const legacySnap = await get(legacyRef);
    if (legacySnap.exists()) {
      list = dedupeFavorites([
        ...list,
        ...normalizeFavoritesList(legacySnap.val()),
      ]);
    }

    return dedupeFavorites(list);
  }

  private async writeFavorites(
    userId: string,
    favorites: FavoriteItem[]
  ): Promise<void> {
    const clean = dedupeFavorites(favorites).map((item) => ({
      uid: item.uid || item.palavra,
      palavra: item.palavra || "",
      letra: item.letra || "",
      descricao: item.descricao || "",
      image: item.image || "",
      video: item.video || "",
      isFavorite: true,
      favoritedAt: item.favoritedAt || Date.now(),
    }));

    // Formato do app: array em favorites/{uid}
    await set(ref(database, this.appFavoritesPath(userId)), clean);

    // Mantém legado em sync (objeto) para não perder leituras antigas
    const asObject: Record<string, FavoriteItem> = {};
    clean.forEach((item) => {
      asObject[favoriteKey(item)] = item;
    });
    await set(ref(database, this.legacyFavoritesPath(userId)), asObject);

    // Contador no perfil (mesmo campo do app mobile)
    await set(ref(database, `users/${userId}/totalFavorites`), clean.length);
  }

  async toggleFavorite(userId: string, verbete: Verbete): Promise<boolean> {
    try {
      if (!database || !userId) return false;

      const key = favoriteKey(verbete);
      if (!key) return false;

      const current = await this.readFavoritesRaw(userId);
      const exists = current.some((f) => favoriteKey(f) === key);

      if (exists) {
        await this.writeFavorites(
          userId,
          current.filter((f) => favoriteKey(f) !== key)
        );
        return false;
      }

      await this.writeFavorites(userId, [
        ...current,
        {
          ...verbete,
          uid: verbete.uid || verbete.palavra,
          isFavorite: true,
          favoritedAt: Date.now(),
        },
      ]);
      return true;
    } catch (error) {
      console.error("Error toggling favorite:", error);
      return false;
    }
  }

  async removeFavorite(userId: string, verbeteId: string): Promise<boolean> {
    try {
      if (!database || !userId || !verbeteId) return false;
      const current = await this.readFavoritesRaw(userId);
      const next = current.filter((f) => favoriteKey(f) !== verbeteId);
      if (next.length === current.length) {
        // ainda tenta limpar chave legada isolada
        await remove(
          ref(database, `${this.legacyFavoritesPath(userId)}/${verbeteId}`)
        );
      }
      await this.writeFavorites(userId, next);
      return true;
    } catch (error) {
      console.error("Error removing favorite:", error);
      return false;
    }
  }

  async getFavorites(userId: string): Promise<Verbete[]> {
    try {
      if (!database || !userId) return [];
      return (await this.readFavoritesRaw(userId)) as Verbete[];
    } catch (error) {
      console.error("Error fetching favorites:", error);
      return [];
    }
  }

  async getFavoritesCount(userId: string): Promise<number> {
    const list = await this.getFavorites(userId);
    return list.length;
  }

  private normalizeVideos(raw: unknown, userId: string): UserVideo[] {
    if (!raw) return [];
    if (Array.isArray(raw)) {
      return raw
        .filter((v) => v && typeof v === "object")
        .map((video: any, index) => ({
          id: String(video.id || `video_${index}`),
          title: video.title || "",
          description: video.description || "",
          videoUrl: video.videoUrl || "",
          thumbnailUrl: video.thumbnailUrl || video.imageUrl,
          imageUrl: video.imageUrl,
          createdAt:
            typeof video.createdAt === "number"
              ? video.createdAt
              : Date.parse(video.createdAt) || Date.now(),
          updatedAt:
            typeof video.updatedAt === "number"
              ? video.updatedAt
              : Date.parse(video.updatedAt) || undefined,
          userId: video.userId || userId,
        }));
    }
    if (typeof raw === "object") {
      return Object.entries(raw as Record<string, any>).map(([id, video]) => ({
        id: String(video?.id || id),
        title: video?.title || "",
        description: video?.description || "",
        videoUrl: video?.videoUrl || "",
        thumbnailUrl: video?.thumbnailUrl || video?.imageUrl,
        imageUrl: video?.imageUrl,
        createdAt:
          typeof video?.createdAt === "number"
            ? video.createdAt
            : Date.parse(video?.createdAt) || Date.now(),
        updatedAt:
          typeof video?.updatedAt === "number"
            ? video.updatedAt
            : Date.parse(video?.updatedAt) || undefined,
        userId: video?.userId || userId,
      }));
    }
    return [];
  }

  async getUserVideos(userId: string): Promise<UserVideo[]> {
    try {
      if (!database || !userId) return [];

      const primarySnap = await get(ref(database, this.appVideosPath(userId)));
      let videos = primarySnap.exists()
        ? this.normalizeVideos(primarySnap.val(), userId)
        : [];

      if (videos.length === 0) {
        const legacySnap = await get(
          ref(database, this.legacyVideosPath(userId))
        );
        if (legacySnap.exists()) {
          videos = this.normalizeVideos(legacySnap.val(), userId);
        }
      }

      return videos.sort((a, b) => b.createdAt - a.createdAt);
    } catch (error) {
      console.error("Error fetching user videos:", error);
      return [];
    }
  }

  async addVideo(
    userId: string,
    video: Omit<UserVideo, "id" | "createdAt" | "userId">
  ): Promise<string | null> {
    try {
      if (!database || !userId) return null;

      const newVideoId = `video-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`;
      const now = Date.now();
      const videoData: UserVideo = {
        ...video,
        id: newVideoId,
        userId,
        createdAt: now,
        updatedAt: now,
      };

      await set(
        ref(database, `${this.appVideosPath(userId)}/${newVideoId}`),
        videoData
      );
      // espelho legado
      await set(
        ref(database, `${this.legacyVideosPath(userId)}/${newVideoId}`),
        videoData
      );
      return newVideoId;
    } catch (error) {
      console.error("Error adding video:", error);
      return null;
    }
  }

  async deleteVideo(userId: string, videoId: string): Promise<boolean> {
    try {
      if (!database || !userId) return false;
      await remove(ref(database, `${this.appVideosPath(userId)}/${videoId}`));
      await remove(
        ref(database, `${this.legacyVideosPath(userId)}/${videoId}`)
      );
      return true;
    } catch (error) {
      console.error("Error deleting video:", error);
      return false;
    }
  }

  async updateVideo(
    userId: string,
    videoId: string,
    updates: { title: string; description: string }
  ): Promise<boolean> {
    try {
      if (!database || !userId) return false;

      const appRef = ref(database, `${this.appVideosPath(userId)}/${videoId}`);
      const legacyRef = ref(
        database,
        `${this.legacyVideosPath(userId)}/${videoId}`
      );

      const appSnap = await get(appRef);
      const legacySnap = await get(legacyRef);
      let current: UserVideo | null = null;
      if (appSnap.exists()) {
        current = appSnap.val() as UserVideo;
      } else if (legacySnap.exists()) {
        current = legacySnap.val() as UserVideo;
      }

      if (!current) return false;

      const next = {
        ...current,
        ...updates,
        id: videoId,
        userId,
        updatedAt: Date.now(),
      };

      await set(appRef, next);
      await set(legacyRef, next);
      return true;
    } catch (error) {
      console.error("Error updating video:", error);
      return false;
    }
  }
}

export const favoritesService = new FavoritesService();
